import { DEFAULT_COUNTRY_CODE } from "@tastyworks/shared-utils/phone/constants"
import i18nIsoCountries, {
  alpha2ToAlpha3,
  type Alpha3Code,
} from "i18n-iso-countries/index.js"
import parsePhoneNumber, {
  type CountryCode,
  type PhoneNumber,
} from "libphonenumber-js/max"
import { mixed, type TestContext } from "yup"

const { alpha3ToAlpha2 } = i18nIsoCountries

export type PhoneNumberConfig = {
  countryCode?: Alpha3Code
  phoneNumber?: string
}

export function isValidPhoneNumber(value: PhoneNumberConfig | undefined) {
  if (!value) {
    return true
  }
  const { phoneNumber, countryCode: alpha3 } = value
  if (!phoneNumber || !alpha3) {
    return true
  }
  const countryCode = alpha3ToAlpha2(alpha3) as CountryCode

  const parsedNumber = parsePhoneNumber(phoneNumber, countryCode)
  // assert the number is in international format
  return !!parsedNumber && parsedNumber.isValid()
}

const phoneTestConfig = {
  name: "validPhoneNumber",
  test: isValidPhoneNumber,
  message: "yup.common.phone.invalid",
}

function isPhoneRequired(
  value: PhoneNumberConfig | undefined,
  context: TestContext
) {
  const { spec } = context.schema
  if (!spec || spec.optional || spec.nullable) {
    return true
  }
  if (!value) return false
  const { phoneNumber, countryCode } = value
  return !!phoneNumber && !!countryCode
}

// ensure that required logic works properly when consumers add `phone().required()`
const phoneRequiredTestConfig = {
  name: "requiredPhoneNumber",
  test: isPhoneRequired,
  message: "yup.common.mixed.required",
}

/**
 * phone number validation
 * @returns yup validation for phone number
 */
export function phone() {
  return mixed<PhoneNumberConfig>()
    .default(undefined)
    .test(phoneTestConfig)
    .test(phoneRequiredTestConfig)
}

function toConfig(parsed: PhoneNumber) {
  return {
    phoneNumber: parsed.nationalNumber,
    countryCode: alpha2ToAlpha3(parsed.country!) as Alpha3Code,
  }
}

export const phoneNumberAdapter = {
  toForm: (model: string) => {
    if (!model) {
      return undefined
    }

    let parsed = parsePhoneNumber(model)
    if (parsed && parsed.isValid()) {
      return toConfig(parsed)
    }

    parsed = parsePhoneNumber(`+${model}`)
    if (parsed && parsed.isValid()) {
      return toConfig(parsed)
    }

    parsed = parsePhoneNumber(model, DEFAULT_COUNTRY_CODE)
    if (parsed && parsed.isValid()) {
      return toConfig(parsed)
    }

    return undefined
  },
  toModel: (form: PhoneNumberConfig | undefined) => {
    if (!form) {
      return ""
    }
    const { phoneNumber, countryCode } = form
    if (!phoneNumber || !countryCode) {
      return ""
    }
    const alpha2 = (alpha3ToAlpha2(countryCode) ?? "US") as CountryCode
    const parsed = parsePhoneNumber(phoneNumber, alpha2)

    return parsed ? parsed.number : ""
  },
}
